/**
 * Sentry Bot API Client
 * Fetches data from the local Sentry Bot API server
 * Only attempts connection in development or when explicitly configured
 */

// Only use API if:
// 1. Explicitly configured via env var, OR
// 2. We're on localhost (development)
const isDevelopment = typeof window !== 'undefined' && 
  (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1');

// Get API URL - prioritize env var (should be set in Vercel), fallback to localhost in dev
const SENTRY_BOT_API_URL = import.meta.env.VITE_SENTRY_BOT_API_URL || 
  (isDevelopment ? 'http://localhost:3001' : 'https://web-production-7d3e.up.railway.app');

// Check if API should be used
const shouldUseApi = () => {
  // Always use API if we have a URL (either from env var or fallback)
  return SENTRY_BOT_API_URL !== null && SENTRY_BOT_API_URL !== undefined && SENTRY_BOT_API_URL !== '';
};

export interface TopGroup {
  chatId: number;
  name?: string; // API returns "name"
  groupName?: string; // Legacy/fallback
  totalPnL: number;
  realizedPnL?: number;
  unrealizedPnL?: number;
  totalPnLSOL?: number;
  memberCount?: number;
  activePositions?: number;
  totalTrades: number;
  topTraders?: string[];
  walletAddress: string;
  photoUrl?: string; // Telegram group photo URL
}

export interface TopTrader {
  userId: number;
  username: string;
  totalPnL: number;
  totalPnLSOL: number;
  tradeCount: number;
  wins: number;
  winRate: number;
  bestTrade: number;
}

export interface GroupStats {
  chatId: number;
  groupName: string;
  walletAddress: string;
  stats: {
    totalTrades: number;
    closedTrades: number;
    activePositions: number;
    wins: number;
    losses: number;
    winRate: number;
    totalRealizedPnL: number;
    totalRealizedPnLSOL: number;
    totalInvested: number;
    totalReturned: number;
    roi: number;
    memberCount: number;
    traderCount: number;
  };
}

export interface DashboardSummary {
  totalGroups: number;
  totalTrades: number;
  activePositions: number;
  totalPnL: number;
  totalPnLSOL: number;
  topGroup: {
    chat_id: number;
    group_name: string;
    wallet_public_key: string;
  } | null;
}

export interface SentryFeeSwapSummary {
  totalSentryBought: number;
  totalSentryBoughtUsd: number;
  totalBuys: number;
  sentryPriceUsd: number;
  wallets: {
    feeWallet?: string;
    swapHandlerWallet?: string;
    [key: string]: string | undefined;
  };
  statsByWallet: Array<{
    walletAddress: string;
    totalSentryTokens: number;
    totalBuys: number;
    totalSolSpent: number;
    scannedTransactions: number;
    truncated: boolean;
  }>;
  scannedTransactions: number;
  truncated: boolean;
  timestamp: string;
}

/**
 * Fetch top trading groups
 */
export async function fetchTopGroups(limit: number = 10): Promise<TopGroup[]> {
  if (!shouldUseApi()) {
    console.warn('[Sentry Bot API] API not configured - VITE_SENTRY_BOT_API_URL not set');
    console.warn('[Sentry Bot API] Current URL:', SENTRY_BOT_API_URL);
    return []; // Return empty if API not available
  }
  
  try {
    const url = `${SENTRY_BOT_API_URL}/api/leaderboard/top-groups?limit=${limit}`;
    console.log('[Sentry Bot API] Fetching top groups from:', url);
    console.log('[Sentry Bot API] Using API URL:', SENTRY_BOT_API_URL);
    
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 5000); // Increased to 5 second timeout
    
    const response = await fetch(url, {
      signal: controller.signal
    });
    clearTimeout(timeoutId);
    
    console.log('[Sentry Bot API] Response status:', response.status, response.statusText);
    
    if (!response.ok) {
      const errorText = await response.text();
      console.error('[Sentry Bot API] HTTP error response:', errorText);
      throw new Error(`HTTP error! status: ${response.status}, body: ${errorText.substring(0, 200)}`);
    }
    
    const result = await response.json();
    console.log('[Sentry Bot API] API response:', result);
    console.log('[Sentry Bot API] Response type:', typeof result, 'Is array:', Array.isArray(result));
    
    // Handle wrapped response: {success: true, data: [...]}
    if (result.success && result.data) {
      console.log('[Sentry Bot API] Top groups data (wrapped):', result.data);
      const groups = Array.isArray(result.data) ? result.data : [];
      console.log('[Sentry Bot API] Returning', groups.length, 'groups');
      return groups;
    }
    
    // Handle direct array response: [...]
    if (Array.isArray(result)) {
      console.log('[Sentry Bot API] Direct array response:', result.length, 'groups');
      return result;
    }
    
    console.error('[Sentry Bot API] Unexpected response format:', result);
    throw new Error(result.error || 'API response format not recognized');
  } catch (error: any) {
    // Log errors for debugging
    if (error.name !== 'AbortError') {
      console.error('[Sentry Bot API] Error fetching top groups:', error.message || error);
      console.error('[Sentry Bot API] Error details:', error);
    } else {
      console.warn('[Sentry Bot API] Request aborted (timeout)');
    }
    return [];
  }
}

/**
 * Fetch top traders
 */
export async function fetchTopTraders(limit: number = 10, chatId?: number): Promise<TopTrader[]> {
  if (!shouldUseApi()) {
    return []; // Silently return empty if API not available
  }
  
  try {
    let url = `${SENTRY_BOT_API_URL}/api/leaderboard/top-traders?limit=${limit}`;
    if (chatId) {
      url += `&chatId=${chatId}`;
    }
    
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 3000);
    
    const response = await fetch(url, {
      signal: controller.signal
    });
    clearTimeout(timeoutId);
    const result = await response.json();
    
    if (result.success) {
      return result.data || [];
    }
    
    throw new Error(result.error || 'Failed to fetch top traders');
  } catch (error: any) {
    // Only log errors in development
    if (isDevelopment) {
      console.warn('[Sentry Bot API] Error fetching top traders:', error.message || error);
    }
    return [];
  }
}

/**
 * Fetch group statistics
 */
export async function fetchGroupStats(chatId: number): Promise<GroupStats | null> {
  if (!shouldUseApi()) {
    return null; // Silently return null if API not available
  }
  
  try {
    const response = await fetch(`${SENTRY_BOT_API_URL}/api/groups/${chatId}/stats`, {
      signal: AbortSignal.timeout(3000) // 3 second timeout
    });
    const result = await response.json();
    
    if (result.success) {
      return result.data || null;
    }
    
    throw new Error(result.error || 'Failed to fetch group stats');
  } catch (error: any) {
    // Only log errors in development
    if (isDevelopment) {
      console.warn('[Sentry Bot API] Error fetching group stats:', error.message || error);
    }
    return null;
  }
}

/**
 * Fetch dashboard summary
 */
export async function fetchDashboardSummary(): Promise<DashboardSummary | null> {
  if (!shouldUseApi()) {
    return null; // Silently return null if API not available
  }
  
  try {
    const response = await fetch(`${SENTRY_BOT_API_URL}/api/dashboard/summary`, {
      signal: AbortSignal.timeout(3000) // 3 second timeout
    });
    const result = await response.json();
    
    if (result.success) {
      return result.data || null;
    }
    
    throw new Error(result.error || 'Failed to fetch dashboard summary');
  } catch (error: any) {
    // Only log errors in development
    if (isDevelopment) {
      console.warn('[Sentry Bot API] Error fetching dashboard summary:', error.message || error);
    }
    return null;
  }
}

/**
 * Fetch cumulative Sentry fee swap summary
 */
export async function fetchSentryFeeSwapSummary(): Promise<SentryFeeSwapSummary | null> {
  if (!shouldUseApi()) {
    return null;
  }

  try {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 15000);
    const response = await fetch(`${SENTRY_BOT_API_URL}/api/sentry-fee-swaps/summary`, {
      signal: controller.signal
    });
    clearTimeout(timeoutId);
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    const result = await response.json();

    if (result && result.success && result.data) {
      return result.data as SentryFeeSwapSummary;
    }

    if (result && typeof result === 'object') {
      return result as SentryFeeSwapSummary;
    }

    throw new Error('Failed to fetch Sentry fee swap summary');
  } catch (error: any) {
    if (isDevelopment) {
      console.warn('[Sentry Bot API] Error fetching fee swap summary:', error.message || error);
    }
    return null;
  }
}

/**
 * Check if API server is available
 * Only attempts connection if API URL is configured
 */
export async function checkApiHealth(): Promise<boolean> {
  if (!shouldUseApi()) {
    return false; // Silently return false if API not configured
  }
  
  try {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 2000);
    
    const response = await fetch(`${SENTRY_BOT_API_URL}/health`, {
      signal: controller.signal
    });
    clearTimeout(timeoutId);
    const result = await response.json();
    return result.status === 'ok';
  } catch (error: any) {
    // Silently fail - don't log errors for health checks (including AbortError)
    return false;
  }
}

export interface SwapEvent {
  id: string;
  timestamp: string;
  message: string;
  color?: 'green' | string;
  username?: string;
  tokenSymbol?: string;
  solAmount?: number;
  tokenAmount?: number;
  type?: 'buy' | 'sell' | 'dca' | 'tp';
  data?: {
    source?: 'pwa' | 'telegram' | 'sentry_buy';
    username?: string;
    tokenSymbol?: string;
    solAmount?: number;
    tokenAmount?: number;
  };
}

/**
 * Fetch swap feed history (past events)
 */
export async function fetchSwapFeedHistory(limit: number = 100): Promise<SwapEvent[]> {
  if (!shouldUseApi()) {
    console.warn('[Sentry Bot API] API not configured for swaps');
    return [];
  }
  
  try {
    const url = `${SENTRY_BOT_API_URL}/api/swap-feed/history?limit=${limit}`;
    console.log('[Sentry Bot API] Fetching swap feed history from:', url);
    
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 5000);
    
    const response = await fetch(url, {
      signal: controller.signal
    });
    clearTimeout(timeoutId);
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    const result = await response.json();
    
    if (result.success && result.events) {
      console.log('[Sentry Bot API] Returning', result.events.length, 'swap events from history');
      return result.events;
    }
    
    return [];
  } catch (error: any) {
    if (error.name !== 'AbortError') {
      console.error('[Sentry Bot API] Error fetching swap feed history:', error.message || error);
    }
    return [];
  }
}

/**
 * Create SSE connection for real-time swap feed
 * Returns EventSource and cleanup function
 */
export function createSwapFeedConnection(
  onEvent: (event: SwapEvent) => void,
  onHistory: (events: SwapEvent[]) => void,
  onError?: (error: Event) => void
): { eventSource: EventSource | null; cleanup: () => void } {
  if (!shouldUseApi()) {
    console.warn('[Sentry Bot API] API not configured for swap feed');
    return { eventSource: null, cleanup: () => {} };
  }
  
  try {
    const url = `${SENTRY_BOT_API_URL}/api/swap-feed`;
    console.log('[Sentry Bot API] Connecting to swap feed SSE:', url);
    
    const eventSource = new EventSource(url);
    
    eventSource.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        
        if (data.type === 'swap') {
          // New swap event
          console.log('[Sentry Bot API] New swap event:', data.event);
          onEvent(data.event);
        } else if (data.type === 'history') {
          // Initial history
          console.log('[Sentry Bot API] Received history:', data.events?.length || 0, 'events');
          if (data.events && Array.isArray(data.events)) {
            onHistory(data.events);
          }
        } else if (data.type === 'keepalive') {
          // Keepalive message, ignore
          console.log('[Sentry Bot API] Keepalive received');
        }
      } catch (error) {
        console.error('[Sentry Bot API] Error parsing SSE message:', error);
      }
    };
    
    eventSource.onerror = (error) => {
      console.error('[Sentry Bot API] SSE connection error:', error);
      if (onError) {
        onError(error);
      }
    };
    
    const cleanup = () => {
      console.log('[Sentry Bot API] Closing swap feed SSE connection');
      eventSource.close();
    };
    
    return { eventSource, cleanup };
  } catch (error: any) {
    console.error('[Sentry Bot API] Error creating swap feed connection:', error);
    return { eventSource: null, cleanup: () => {} };
  }
}